"use client";

import { useState, useRef, useEffect } from "react";
import { themes } from "@/lib/themes";

interface ThemePickerProps {
  value: string;
  onChange: (themeId: string) => void;
}

export default function ThemePicker({ value, onChange }: ThemePickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selectedTheme = themes.find((t) => t.id === value) ?? themes[0];

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="rounded-lg border border-border p-1.5 px-3 font-medium text-sm text-foreground cursor-pointer flex items-center gap-2 hover:bg-muted transition-colors"
      >
        <span className="w-2 h-2 rounded-full bg-foreground shrink-0" />
        <span>{selectedTheme.name}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          className={`text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : "rotate-0"}`}
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div className="absolute top-[calc(100%+4px)] right-0 bg-card border border-border rounded-lg shadow-lg p-1 min-w-[200px] z-50 animate-[dropdownEnter_0.15s_ease-out]">
          {themes.map((theme) => (
            <button
              key={theme.id}
              onClick={() => {
                onChange(theme.id);
                setOpen(false);
              }}
              className={`block w-full text-left py-2 px-3 rounded border-none cursor-pointer transition-colors border-l-2 ${
                theme.id === value
                  ? "bg-muted border-l-foreground"
                  : "bg-transparent border-l-transparent hover:bg-muted"
              }`}
            >
              <div className="font-medium text-sm text-card-foreground">
                {theme.name}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {theme.description}
              </div>
            </button>
          ))}
        </div>
      )}

    </div>
  );
}
